import React, { useCallback, useEffect, useMemo, useState } from "react";
import { salaryEnums } from "../../../Constant/HR/staffSalary";
import { revisionHistoryApi } from "../../../Api/salaryApi";
import {
  checkStatusCodeSuccess,
  extractDate,
  getItem,
  getLocalItem,
  handleResponse,
  isUserProfile,
  RupeesSymbol,
} from "../../../Constant/common";
import { toast } from "react-toastify";
import TableContainer from "../../../BaseComponents/BaseTable";
import { notFound, Search } from "../../../Constant";
import { useNavigate, useParams } from "react-router-dom";
import { salaryRevisionHistoryTitle } from "../../../Constant/title";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";
import BackButton from "../../../BaseComponents/BackButton";
import { AlertCircle, Clock } from "react-feather";

const SalaryHistory = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = usePageLoader();
  const [totalPages, setTotalPages] = useState();
  const [totalRecords, setTotalRecords] = useState();
  const [totalNumberOfRows, setTotalNumberOfRows] = useState();
  const [customPageSize, setCustomPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [columnName, setColumnName] = useState("id");
  const [searchValue, setSearchValue] = useState("");
  const [salaryHistory, setSalaryHistory] = useState(null);
  const [historySearchList, setHistorySearchList] = useState(null);

  const isProfile = JSON.parse(getItem(isUserProfile) || false);
  const profileId = getLocalItem("id");
  const finalId = isProfile ? profileId : staffId;

  useEffect(() => {
    document.title = salaryRevisionHistoryTitle;
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    revisionHistoryApi(finalId, {
      order: [columnName, sortOrder],
      pageSize: customPageSize,
      pageNumber: currentPage,
      search: searchValue,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp.statusCode)) {
          setSalaryHistory(resp?.data?.listOfReviseStaffSalary);
          setHistorySearchList(resp?.data?.listOfReviseStaffSalary);
          setTotalPages(resp?.data?.totalPages);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        } else {
          setSalaryHistory([]);
          setHistorySearchList([]);
          setTotalPages(1);
          setTotalRecords(0);
          if (resp?.statusCode !== 404) toast.error(resp?.message);
        }
      })
      .catch((err) => {
        setSalaryHistory([]);
        setHistorySearchList([]);
        setTotalPages(1);
        setTotalRecords(0);
        const status = err?.response?.status ?? err?.response?.data?.statusCode;
        if (status !== 404) {
          toast.error(err?.response?.data?.message ?? err?.message);
        }
      })
      .finally(() => setLoading(false));
  }, [columnName, currentPage, customPageSize, finalId, searchValue, sortOrder, setLoading]);

  useEffect(() => {
    const t = setTimeout(fetchData, 0);
    return () => clearTimeout(t);
  }, [fetchData]);

  const columns = useMemo(
    () => [
      {
        header: salaryEnums.SR_NO,
        accessorKey: "id",
        cell: (c) => c.row.index + 1,
        enableColumnFilter: false,
      },
      {
        header: salaryEnums.PREVIOUS_SALARY,
        accessorKey: "previous_salary",
        cell: (c) => RupeesSymbol(c.row.original.previous_salary) ?? handleResponse.nullData,
        enableColumnFilter: false,
      },
      {
        header: salaryEnums.REVISED_SALARY,
        accessorKey: "revised_monthly_salary",
        cell: (c) => RupeesSymbol(c.row.original.revised_monthly_salary) ?? handleResponse.nullData,
        enableColumnFilter: false,
      },
      {
        header: salaryEnums.PERCENT_CHANGE,
        accessorKey: "revised_percentage",
        enableColumnFilter: false,
      },
      {
        header: salaryEnums.EFFECTIVE_DATE,
        accessorKey: "effective_date",
        cell: (c) => extractDate(c.row.original.effective_date) ?? handleResponse.nullData,
        enableColumnFilter: false,
      },
      {
        header: salaryEnums.CREATED_DATE,
        accessorKey: "created_date",
        cell: (c) => extractDate(c.row.original.created_date) ?? handleResponse.nullData,
        enableColumnFilter: false,
      },
      { header: salaryEnums.REVISED_BY, accessorKey: "authUser.name", enableColumnFilter: false },
    ],
    [],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Clock size={18} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {salaryEnums.REVISION_HISTORY}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords
                ? `${totalRecords} revision${totalRecords !== 1 ? "s" : ""}`
                : "Salary revision records"}
            </p>
          </div>
        </div>
        <BackButton
          onClick={() =>
            isProfile ? navigate("/salaryStructure") : navigate(`/salaryStructure/${staffId}`)
          }
        />
      </div>

      {/* Table */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {salaryHistory !== null && (salaryHistory?.length > 0 || searchValue) ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            fetchData={(p) => setCurrentPage(p)}
            columns={columns}
            data={searchValue ? historySearchList : salaryHistory || []}
            isGlobalFilter={true}
            manualFiltering={true}
            onSearch={(v) => {
              if (v !== searchValue) {
                setSearchValue(v);
                setCurrentPage(1);
              }
            }}
            customPageSize={customPageSize}
            setCustomPageSize={setCustomPageSize}
            manualPagination={true}
            fetchSortingData={(p, id, order) => {
              setCurrentPage(p);
              setColumnName(id);
              setSortOrder(order);
            }}
            SearchPlaceholder={Search}
            shouldLineBreak={false}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryHistory;
